/**
 * ที่เดียวในโปรเจกต์ที่มี MQTT topic เป็น string
 * CLAUDE.md กฎเหล็กข้อ 1: ห้ามพิมพ์ topic literal นอกไฟล์นี้ — CI มี grep เช็ค
 */
import { MQTT_PREFIX, type SceneId } from "./constants.js";

export type PlayerId = string; // เช่น "p_8f2a1c"

export const topics = {
  // ---- มือถือ → NB (ต่อฉาก ต่อผู้เล่น) ----
  input: (scene: SceneId, pid: PlayerId) =>
    `${MQTT_PREFIX}/scene/${scene}/player/${pid}/input`,
  spawn: (scene: SceneId, pid: PlayerId) =>
    `${MQTT_PREFIX}/scene/${scene}/player/${pid}/spawn`,
  release: (scene: SceneId, pid: PlayerId) =>
    `${MQTT_PREFIX}/scene/${scene}/player/${pid}/release`,
  sync: (scene: SceneId, pid: PlayerId) =>
    `${MQTT_PREFIX}/scene/${scene}/player/${pid}/sync`,

  // ---- NB → มือถือ ----
  pos: (pid: PlayerId) => `${MQTT_PREFIX}/player/${pid}/pos`,
  edge: (pid: PlayerId) => `${MQTT_PREFIX}/player/${pid}/edge`,
  act: (pid: PlayerId) => `${MQTT_PREFIX}/player/${pid}/act`,

  // ---- retained ----
  profile: (pid: PlayerId) => `${MQTT_PREFIX}/player/${pid}/profile`,
  screenStatus: (scene: SceneId) => `${MQTT_PREFIX}/screen/${scene}/status`,

  // ---- ผู้ดูแล ----
  sysCmd: () => `${MQTT_PREFIX}/sys/cmd`,
} as const;

/** wildcard ที่ NB ของ scene N ต้อง subscribe */
export function screenSubscriptions(scene: SceneId): string[] {
  return [
    `${MQTT_PREFIX}/scene/${scene}/player/+/input`,
    `${MQTT_PREFIX}/scene/${scene}/player/+/spawn`,
    `${MQTT_PREFIX}/scene/${scene}/player/+/release`,
    `${MQTT_PREFIX}/scene/${scene}/player/+/sync`,
    // retained → จอที่เพิ่งเปิดรู้ URL ตัวละครทันที
    `${MQTT_PREFIX}/player/+/profile`,
    `${MQTT_PREFIX}/sys/cmd`,
  ];
}

/** มือถือ subscribe เฉพาะของตัวเอง + สถานะจอทุกจอ */
export function mobileSubscriptions(pid: PlayerId): string[] {
  return [
    topics.pos(pid),
    topics.edge(pid),
    topics.act(pid),
    `${MQTT_PREFIX}/screen/+/status`,
    `${MQTT_PREFIX}/sys/cmd`,
  ];
}

/** ดึง playerId ออกจาก topic ที่มี wildcard (NB ใช้ตอนรับข้อความ) */
export function pidFromTopic(topic: string): PlayerId | null {
  const parts = topic.split("/");
  const i = parts.indexOf("player");
  if (i < 0) return null;
  return parts[i + 1] ?? null;
}

export function sceneFromTopic(topic: string): SceneId | null {
  const parts = topic.split("/");
  const i = parts.indexOf("scene");
  if (i < 0) return null;
  const n = Number(parts[i + 1]);
  return Number.isInteger(n) && n >= 0 && n <= 5 ? (n as SceneId) : null;
}

// ---- clientId ----
export const clientIds = {
  mobile: (pid: PlayerId) => `py-m-${pid}`,
  /** rand4 กัน id ชนตอน refresh และตอนเปิดจอ present พร้อมจอจริง */
  screen: (scene: SceneId, rand4: string) => `py-s-${scene}-${rand4}`,
} as const;

export function rand4(): string {
  const b = new Uint8Array(2);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

export function newPlayerId(): PlayerId {
  const b = new Uint8Array(3);
  crypto.getRandomValues(b);
  return `p_${Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("")}`;
}
